import { useState, useCallback, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, BarChart3, Radio, QrCode, Settings, ChevronDown, X } from "lucide-react";
import { API, axios } from "@/api";
import { useSSEWithFallback } from "@/hooks/useSSEWithFallback";
import { useAuth } from "@/contexts/AuthContext";

const NAV_ITEMS = [
  { to: "/vendor", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/vendor/analytics", icon: BarChart3, label: "Analytics" },
  { to: "/vendor/go-live", icon: null, label: "LIVE", isCenter: true },
  { to: "/vendor/qr", icon: QrCode, label: "QR Code" },
  { to: "/vendor/settings", icon: Settings, label: "Settings" },
];

export default function VendorNavBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [liveStatus, setLiveStatus] = useState("offline");
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [portfolio, setPortfolio] = useState(null);
  const [activeTruck, setActiveTruck] = useState(null);

  const isPortfolio = user?.is_portfolio_vendor;

  useEffect(() => {
    if (!isPortfolio) return;
    axios.get(`${API}/vendor/portfolio`).then(res => {
      if (res.data?.is_portfolio_vendor) {
        setPortfolio(res.data);
        if (!activeTruck && res.data.trucks?.length) {
          setActiveTruck(res.data.trucks[0]);
        }
      }
    }).catch(() => {});
  }, [isPortfolio]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vendor/live-status`);
      if (res.data.active) {
        setLiveStatus(res.data.status || "live");
      } else {
        setLiveStatus("offline");
      }
    } catch { /* ignore */ }
  }, []);

  useSSEWithFallback(
    "vendor_session_changed",
    (data) => setLiveStatus(data.session_status || "offline"),
    fetchStatus,
  );

  const switchTruck = (truck) => {
    setActiveTruck(truck);
    setShowSwitcher(false);
    // Store active truck context for the dashboard
    sessionStorage.setItem("active_truck_id", truck.business_id);
    window.dispatchEvent(new CustomEvent("truck-switched", { detail: truck }));
  };

  const centerColor = liveStatus === "live" ? "bg-[#22C55E]" : liveStatus === "closing" ? "bg-amber-400" : "bg-[#22C55E]";
  const centerPulse = liveStatus === "live" || liveStatus === "closing";
  const centerLabel = liveStatus === "live" ? "LIVE" : liveStatus === "closing" ? "SOON" : "GO";

  return (
    <>
      {/* Truck Switcher Bottom Sheet */}
      {showSwitcher && portfolio && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end" data-testid="truck-switcher-overlay" onClick={() => setShowSwitcher(false)}>
          <div className="w-full max-w-md mx-auto bg-[#1C1C1E] rounded-t-2xl border-t border-white/10 p-4 pb-8 animate-in slide-in-from-bottom" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-sm">Switch Truck</h3>
              <button onClick={() => setShowSwitcher(false)} className="text-white/40" data-testid="truck-switcher-close"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2">
              {portfolio.trucks.map(truck => {
                const color = portfolio.color_assignments?.[truck.business_id] || "#F59E0B";
                const isActive = activeTruck?.business_id === truck.business_id;
                return (
                  <button
                    key={truck.business_id}
                    onClick={() => switchTruck(truck)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${isActive ? "bg-white/10 border border-white/20" : "hover:bg-white/5"}`}
                    data-testid={`truck-option-${truck.business_id}`}
                  >
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                    <div className="text-left flex-1">
                      <p className="text-sm font-medium text-white">{truck.name}</p>
                      {truck.cuisine_tags?.length > 0 && (
                        <p className="text-[10px] text-white/40">{truck.cuisine_tags.slice(0, 3).join(", ")}</p>
                      )}
                    </div>
                    {isActive && <span className="text-[9px] text-amber-500 font-bold">ACTIVE</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Portfolio truck name bar above nav */}
      {isPortfolio && activeTruck && (
        <div className="fixed bottom-[60px] left-0 right-0 z-40 max-w-md mx-auto">
          <button
            onClick={() => setShowSwitcher(true)}
            className="w-full flex items-center justify-center gap-2 py-1.5 bg-[#2C2C2E] border-t border-white/5"
            data-testid="truck-switcher-btn"
          >
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: portfolio?.color_assignments?.[activeTruck.business_id] || "#F59E0B" }} />
            <span className="text-[11px] font-medium text-white/70">{activeTruck.name}</span>
            <ChevronDown className="w-3 h-3 text-white/40" />
          </button>
        </div>
      )}

      <nav className="fixed bottom-0 left-0 right-0 z-40 bg-[#1C1C1E] border-t border-white/10 max-w-md mx-auto" data-testid="vendor-nav-bar">
        <div className="flex items-end justify-around px-2 pb-1 pt-1 relative">
          {NAV_ITEMS.map((item) => {
            if (item.isCenter) {
              return (
                <button
                  key={item.to}
                  onClick={() => navigate(item.to)}
                  className="relative -mt-8 flex flex-col items-center"
                  data-testid="vendor-nav-go-live"
                >
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all ${centerColor} ${centerPulse ? "animate-pulse" : ""}`}
                    style={{ boxShadow: liveStatus === "live" ? "0 4px 20px rgba(34,197,94,0.5)" : liveStatus === "closing" ? "0 4px 20px rgba(251,191,36,0.4)" : "0 4px 15px rgba(34,197,94,0.3)" }}>
                    <Radio className="w-7 h-7 text-white" />
                  </div>
                  <span className={`text-[9px] font-bold mt-0.5 ${liveStatus === "live" ? "text-[#22C55E]" : liveStatus === "closing" ? "text-amber-400" : "text-white"}`}>
                    {centerLabel}
                  </span>
                </button>
              );
            }

            const Icon = item.icon;
            const isActive = location.pathname === item.to;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className="flex flex-col items-center py-1.5 px-2 min-w-[52px]"
                data-testid={`vendor-nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <Icon className={`w-5 h-5 transition-colors ${isActive ? "text-[#F59E0B]" : "text-white/40"}`} />
                <span className={`text-[9px] mt-0.5 font-medium transition-colors ${isActive ? "text-[#F59E0B]" : "text-white/40"}`}>
                  {item.label}
                </span>
              </NavLink>
            );
          })}
        </div>
      </nav>
    </>
  );
}
